// Data-driven renderer for structured legal / info page content
// Consumes the content shape defined in /app/frontend/src/mock/legalContent.js
import LegalLayout, { Section, Bullet } from './LegalLayout';
import { Flame, Users, Heart, Award } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const iconMap = { personal: Heart, simple: Flame, inclusive: Users, consistent: Award };

const LegalContentRenderer = ({ content }) => {
  const { tr } = useLang();

  return (
    <LegalLayout
      title={tr(content.title)}
      subtitle={tr(content.subtitle)}
      lastUpdated={content.lastUpdated ? tr(content.lastUpdated) : undefined}
    >
      {content.intro && <p>{tr(content.intro)}</p>}

      {content.sections?.map((sec, i) => (
        <Section key={i} title={tr(sec.title)}>
          {sec.paragraphs?.map((p, j) => (
            <p key={`p-${j}`}>{tr(p)}</p>
          ))}

          {sec.bullets && (
            <ul className="space-y-2">
              {sec.bullets.map((b, j) => (
                <Bullet key={`b-${j}`}>{tr(b)}</Bullet>
              ))}
            </ul>
          )}

          {sec.ordered && (
            <ol className="space-y-2 list-decimal list-inside">
              {sec.ordered.map((o, j) => (
                <li key={`o-${j}`}>{tr(o)}</li>
              ))}
            </ol>
          )}

          {sec.footer && <p>{tr(sec.footer)}</p>}

          {sec.emails && (
            <p>
              {sec.emails.map((em, j) => (
                <span key={j}>
                  {tr(em.label)}:{' '}
                  <a className="text-[#ff4e15] hover:underline" href={`mailto:${em.addr}`}>
                    {em.addr}
                  </a>
                  {j < sec.emails.length - 1 ? <br /> : null}
                </span>
              ))}
            </p>
          )}

          {sec.values && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              {sec.values.map((v) => {
                const Icon = iconMap[v.key] || Flame;
                return (
                  <div key={v.key} className="p-4 bg-[#1a1b1e] border border-[#2a2b2f] rounded-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-sm bg-[#ff4e15]/15 flex items-center justify-center">
                        <Icon size={16} className="text-[#ff4e15]" />
                      </div>
                      <div className="font-bold text-white">{tr(v.title)}</div>
                    </div>
                    <div className="text-sm text-white/70">{tr(v.text)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      ))}
    </LegalLayout>
  );
};

export default LegalContentRenderer;
